import { createContext } from "react";
import IBaseListingResponse from "../../utils/api/base-listing-response";
import {
  DEFAULT_FUNCTION,
  DEFAULT_QUERY,
} from "../../utils/helpers/constants";
import { IScaleMovementQuery, IScaleQuery } from "../../models/scale/query";
import {
  IScale,
  IScaleDetails,
  IScaleMovement,
  IScalePendingChanges,
  IScalePendingChangesSummary,
  IScaleSlot,
} from "../../models/scale/response";
import { ICreateScale, IUpdateScale } from "../../models/scale/request";

export type ScaleLoading =
  | "list"
  | "details"
  | "create"
  | "update"
  | "delete"
  | "slots"
  | "detach"
  | "movements"
  | "pending"
  | "markFed"
  | "export";

export interface IInternalState {
  loading: ScaleLoading[];

  list?: IBaseListingResponse<IScale>;
  query: IScaleQuery;

  details?: IScaleDetails;

  /** The whole device table in hotkey order — never paginated, never filtered:
   *  it mirrors the exported file row for row. */
  slots?: IScaleSlot[];

  pendingChanges?: IScalePendingChanges;
  /** Keyed by scale id, for the badges on the list page. */
  pendingSummaries: Record<number, IScalePendingChangesSummary>;

  movements?: IBaseListingResponse<IScaleMovement>;
  movementsQuery: IScaleMovementQuery;
}

export const internalState: IInternalState = {
  loading: [],
  query: DEFAULT_QUERY,
  pendingSummaries: {},
  movementsQuery: DEFAULT_QUERY,
};

export interface IExternalState extends IInternalState {
  actions: {
    getData: () => void;
    getDetails: (id: number) => void;
    setDetails: (data?: IScaleDetails) => void;

    createScale: (request: ICreateScale) => void;
    updateScale: (id: number, request: IUpdateScale) => void;
    deleteScale: (id: number) => void;

    getSlots: (id: number) => void;
    detachProduct: (id: number, productId: number) => void;

    getMovements: (id: number) => void;
    setMovementsQuery: (query: IScaleMovementQuery) => void;

    getPendingChanges: (id: number) => void;
    getListPendingChanges: (scaleIds: number[]) => void;

    exportScale: (id: number) => void;
    markFed: (id: number) => void;

    setSearch: (search?: string) => void;
    setQuery: (query: IScaleQuery) => void;
  };
}

export const externalState: IExternalState = {
  ...internalState,
  actions: {
    getData: DEFAULT_FUNCTION,
    getDetails: DEFAULT_FUNCTION,
    setDetails: DEFAULT_FUNCTION,

    createScale: DEFAULT_FUNCTION,
    updateScale: DEFAULT_FUNCTION,
    deleteScale: DEFAULT_FUNCTION,

    getSlots: DEFAULT_FUNCTION,
    detachProduct: DEFAULT_FUNCTION,

    getMovements: DEFAULT_FUNCTION,
    setMovementsQuery: DEFAULT_FUNCTION,

    getPendingChanges: DEFAULT_FUNCTION,
    getListPendingChanges: DEFAULT_FUNCTION,

    exportScale: DEFAULT_FUNCTION,
    markFed: DEFAULT_FUNCTION,

    setSearch: DEFAULT_FUNCTION,
    setQuery: DEFAULT_FUNCTION,
  },
};

const ScaleContext = createContext(externalState);

export default ScaleContext;
